import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { noop } from 'lodash';
import { addLocation } from '../utils/map';

const useInitializeMapEffect = ({
  mapContainer,
  mapRef,
  mapLoaded,
  setMapLoaded,
  lat,
  setLat = noop,
  lng,
  setLng = noop,
  zoom,
  setZoom = noop,
  onLoad = noop,
  onUnload = noop,
  // These controls default to being enabled but can be disabled individually:
  controls: {
    geolocation = true,
    compass = true,
    fullscreen = true,
    navigationLocation = 'top-right',
  } = {},
  ...mapProps
}) => {
  // Create a ref for mapLoaded so that we can access the up-to-date value in useEffect.
  const mapLoadedRef = useRef(mapLoaded);
  mapLoadedRef.current = mapLoaded;

  useEffect(() => {
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_API_KEY;

    // eslint-disable-next-line no-param-reassign
    mapRef.current = new mapboxgl.Map({
      container: mapContainer.current,
      center: [lng, lat],
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      zoom,
      ...mapProps,
    });
    if (fullscreen) {
      mapRef.current.addControl(new mapboxgl.FullscreenControl());
    }
    if (geolocation) {
      addLocation(mapRef.current);
    }
    mapRef.current.addControl(
      new mapboxgl.NavigationControl({ showCompass: compass }),
      navigationLocation,
    );

    mapRef.current.on('move', () => {
      setLng(mapRef.current.getCenter().lng.toFixed(4));
      setLat(mapRef.current.getCenter().lat.toFixed(4));
      setZoom(mapRef.current.getZoom().toFixed(2));
    });

    mapRef.current.on('load', () => {
      if (!mapLoadedRef.current) {
        onLoad(mapRef);
        setMapLoaded(true);
      }
    });

    return () => {
      onUnload(mapRef);

      mapRef.current.remove();
      // eslint-disable-next-line no-param-reassign
      mapRef.current = null;
      setMapLoaded(false);
    };
    // initialize map only once
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
};

export default useInitializeMapEffect;
